/* lib/router.jsx — минимальный, API-совместимый с react-router-dom v6 роутер.
   ПРИЧИНА: в прототипе нет Vite-бандлера и надёжного UMD react-router-dom.
   Реализует ровно ту поверхность API, что использует прототип:
   BrowserRouter, Routes, Route, Outlet, Navigate, Link, NavLink,
   useNavigate, useLocation, useParams, useSearchParams.

   РЕЖИМ HISTORY (cutover на боевой :5173 / прод-домен). Маршрут живёт в реальном пути
   (`/kitchen?tab=recipes&edit=ttk-301`) через History API (pushState/replaceState), а не во
   фрагменте. ТРЕБУЕТ SPA-fallback на сервере (Vite dev — встроенный appType:'spa'; nginx —
   `try_files $uri /index.html`), иначе refresh/deep-link на вложенных путях дадут 404 хоста.
   Имена и сигнатуры — 1:1 с react-router v6; ссылки из писем/Робокассы (обычные пути,
   `/verify-email?…`, `/checkout/success?…`) теперь резолвятся напрямую. */

import React from 'react';

  const { createContext, useContext, useState, useEffect, useCallback, useMemo, createElement } = React;

  // ---- история (hash) ----
  const LocationContext = createContext(null);
  const RouteContext = createContext({ outlet: null, params: {} });

  function useLocation() {
    return useContext(LocationContext).location;
  }
  function useNavigate() {
    return useContext(LocationContext).navigate;
  }

  // logical-маршрут берём из реального пути (history-режим): pathname + search
  function readLocation() {
    return {
      pathname: window.location.pathname || '/',
      search: window.location.search || '',
      hash: '',
      state: (window.history.state && window.history.state.__rr) || null,
    };
  }

  // logical target (строка «/kitchen?tab=…») → запись в history как реальный путь
  function writeHash(target, opts = {}) {
    const url = target.startsWith('/') ? target : '/' + target;
    const st = { __rr: opts.state ?? null };
    if (opts.replace) window.history.replaceState(st, '', url);
    else window.history.pushState(st, '', url);
  }

  function BrowserRouter({ children }) {
    const [location, setLocation] = useState(() => readLocation());

    useEffect(() => {
      const sync = () => setLocation(readLocation());
      window.addEventListener('popstate', sync);
      // history-режим: путь уже канонический; нормализуем только router-state на старте
      if (!(window.history.state && '__rr' in window.history.state)) {
        window.history.replaceState({ __rr: null }, '', window.location.pathname + window.location.search);
      }
      return () => {
        window.removeEventListener('popstate', sync);
      };
    }, []);

    const navigate = useCallback((to, opts = {}) => {
      if (typeof to === 'number') { window.history.go(to); return; }
      const target = resolveTo(to);
      // счётчик переходов внутри приложения (для useBackNav: отличить deep-link от истории).
      // считаем только push'и (не replace, не редиректы Navigate) — реальные шаги пользователя.
      if (!opts.replace) window.__rrNavDepth = (window.__rrNavDepth || 0) + 1;
      writeHash(target, opts);
      setLocation(readLocation()); // pushState/replaceState не шлют событий — обновляем вручную
      if (!opts.replace && !opts.preventScrollReset) {
        const main = document.querySelector('[data-scroll-main]');
        if (main) main.scrollTop = 0;
      }
    }, []);

    const value = useMemo(() => ({ location, navigate }), [location, navigate]);
    return createElement(LocationContext.Provider, { value }, children);
  }

  // объект/строка → logical-строка «/path?search#hash»
  function resolveTo(to) {
    if (typeof to === 'string') return to.startsWith('/') ? to : '/' + to;
    const { pathname = '/', search = '', hash = '' } = to;
    const s = search ? (search.startsWith('?') ? search : '?' + search) : '';
    const h = hash ? (hash.startsWith('#') ? hash : '#' + hash) : '';
    return (pathname.startsWith('/') ? pathname : '/' + pathname) + s + h;
  }

  // ---- сбор конфигурации маршрутов из JSX-детей ----
  function childrenToRoutes(children) {
    const routes = [];
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;
      const { path, index, element } = child.props;
      const route = {
        path: index ? undefined : path,
        index: !!index,
        element: element ?? null,
        children: child.props.children ? childrenToRoutes(child.props.children) : [],
      };
      routes.push(route);
    });
    return routes;
  }

  // разворачиваем дерево в плоские «ветви» (root → leaf)
  function flattenRoutes(routes, parent = { fullPath: '', chain: [] }) {
    let branches = [];
    routes.forEach((route) => {
      const seg = route.index ? '' : (route.path || '');
      const joined = joinPaths(parent.fullPath, seg);
      const chain = parent.chain.concat(route);
      if (route.children && route.children.length) {
        branches = branches.concat(flattenRoutes(route.children, { fullPath: joined, chain }));
      } else {
        branches.push({ fullPath: joined || '/', chain, index: route.index, raw: seg });
      }
    });
    return branches;
  }

  function joinPaths(a, b) {
    if (!b) return a || '';
    if (!a) return b.startsWith('/') ? b : '/' + b;
    return (a.replace(/\/$/, '') + '/' + b.replace(/^\//, ''));
  }

  // компиляция паттерна → regex + ключи (посегментно, корректно для * и :param)
  function compile(pattern) {
    const keys = [];
    let p = pattern.replace(/\/$/, '');
    if (p === '') p = '/';
    const regexStr = p.split('/').map((seg) => {
      if (seg === '') return '';
      if (seg === '*') { keys.push('*'); return '(.*)'; }
      if (seg.startsWith(':')) { keys.push(seg.slice(1)); return '([^/]+)'; }
      return seg.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    }).join('/');
    return { regex: new RegExp('^' + regexStr + '/?$'), keys, pattern: p };
  }

  // ранжирование: больше статических сегментов → выше; splat → ниже
  function score(branch) {
    const segs = branch.fullPath.split('/').filter(Boolean);
    let s = 0;
    segs.forEach((seg) => {
      if (seg === '*') s -= 4;
      else if (seg.startsWith(':')) s += 4;
      else s += 8;
    });
    if (branch.index) s += 2;
    return s;
  }

  function matchBranch(branch, pathname) {
    const { regex, keys } = compile(branch.fullPath);
    const m = regex.exec(pathname);
    if (!m) return null;
    const params = {};
    keys.forEach((k, i) => {
      const raw = m[i + 1] != null ? m[i + 1] : '';
      try { params[k] = decodeURIComponent(raw); } catch (e) { params[k] = raw; }
    });
    return params;
  }

  function Routes({ children }) {
    const { location } = useContext(LocationContext);
    const routes = useMemo(() => childrenToRoutes(children), [children]);
    const branches = useMemo(() => flattenRoutes(routes).sort((a, b) => score(b) - score(a)), [routes]);

    let matched = null;
    for (const branch of branches) {
      const params = matchBranch(branch, location.pathname);
      if (params) { matched = { branch, params }; break; }
    }
    if (!matched) return null;

    const chain = matched.branch.chain;
    function renderChain(i) {
      const route = chain[i];
      if (!route) return null;
      const outlet = renderChain(i + 1);
      const el = route.element != null ? route.element : createElement(Outlet, null);
      return createElement(RouteContext.Provider, { value: { outlet, params: matched.params } }, el);
    }
    return renderChain(0);
  }

  function Route() { return null; } // конфигурационный, не рендерится напрямую

  function Outlet() {
    const ctx = useContext(RouteContext);
    return ctx.outlet || null;
  }

  function useParams() {
    return useContext(RouteContext).params || {};
  }

  function Navigate({ to, replace = true, state }) {
    const navigate = useNavigate();
    useEffect(() => { navigate(to, { replace, state }); }, [to]);
    return null;
  }

  function Link({ to, children, className, onClick, ...rest }) {
    const navigate = useNavigate();
    // реальный путь → middle-click / «открыть в новой вкладке» дают корректный URL (history)
    const href = resolveTo(to);
    const handle = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      if (onClick) onClick(e);
      navigate(to);
    };
    return createElement('a', { href, className, onClick: handle, ...rest }, children);
  }

  function NavLink({ to, children, className, end, ...rest }) {
    const location = useLocation();
    const targetPath = (typeof to === 'string' ? to : to.pathname).split('?')[0].split('#')[0];
    const cur = location.pathname;
    const isActive = end
      ? cur === targetPath
      : cur === targetPath || cur.startsWith(targetPath.replace(/\/$/, '') + '/');
    const cls = typeof className === 'function' ? className({ isActive }) : className;
    const kids = typeof children === 'function' ? children({ isActive }) : children;
    return createElement(Link, {
      to,
      className: cls,
      'aria-current': isActive ? 'page' : undefined,
      'data-active': isActive ? '' : undefined,
      ...rest,
    }, kids);
  }

  // useSearchParams — обёртка над URLSearchParams синхронно с logical-location
  function useSearchParams() {
    const { location, navigate } = useContext(LocationContext);
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const setSearchParams = useCallback((next, opts = {}) => {
      // Функциональная форма обязана видеть АКТУАЛЬНЫЙ адрес, а не тот, что был на
      // рендере, где создан этот колбэк. navigate() пишет в history синхронно
      // (writeHash → pushState), а React-состояние роутера догоняет лишь следующим
      // рендером. Пока `prev` собирался из замкнутого location.search, два писателя
      // в ОДНОМ коммите затирали друг друга: в плеере «Академии» resume-эффект
      // дописывал ?lesson=…, а сторож возврата с оплаты тут же снимал ?purchase=,
      // видя адрес ДО первой правки, — и ученик после оплаты попадал на пустой
      // экран вместо своего урока. Дефект общий для приложения: он ждал любой
      // экран, где query пишут дважды за коммит.
      const live = readLocation();
      const sp = typeof next === 'function' ? next(new URLSearchParams(live.search)) : new URLSearchParams(next);
      const str = sp.toString();
      navigate({ pathname: live.pathname, search: str ? '?' + str : '', hash: '' }, { replace: opts.replace, preventScrollReset: true });
    }, [navigate]);
    return [searchParams, setSearchParams];
  }

  export const RR = {
    BrowserRouter, Routes, Route, Outlet, Navigate, Link, NavLink,
    useNavigate, useLocation, useParams, useSearchParams,
  };
